import { useQuery } from 'react-query'
import { fetchAPI } from '@/lib/api'

import { QUERY_KEYS } from './constants'

export interface UserStatLog {
  company_name: string
  email: string
  phone_number: string
  package_name: string
  start_date: string
  expiry: string
  user_type: string
  cancel_reason: string
}
export interface UserStatsResponse {
  report_period: string
  total_users: number
  renewals: number
  renewals_percentage: number
  churns: number
  churns_percentage: number
  churn_but_renew: number
  churn_but_renew_percentage: number
  users: UserStatLog[]
}

export const useUserStats = ({
  date,
  enabled = true,
}: {
  date?: string
  enabled?: boolean
}) => {
  return useQuery<UserStatsResponse>({
    queryKey: [QUERY_KEYS.WEEKLY_USER_STATS, date],
    enabled: !!date && enabled,
    queryFn: async () => {
      const { data } = await fetchAPI<UserStatsResponse>({
        path: '/admins/reports/user_stats',
        params: { date },
      })
      return data
    },
  })
}

export interface WeekStatOption {
  label: string
  value: string
}

export const useAvailableWeeks = () => {
  return useQuery<WeekStatOption[]>({
    queryKey: [QUERY_KEYS.WEEK_REPORT_OPTIONS],
    queryFn: async () => {
      const { data } = await fetchAPI<WeekStatOption[]>({
        path: '/admins/reports/weeks',
      })
      return data
    },
  })
}

export interface DateRange {
  from: string
  to: string
}

export interface SummaryReport {
  year_range: DateRange
  month_range: DateRange
  total_users: number
  new_users: number
  new_users_percentage: number
  churns: number
  churns_percentage: number
  churn_but_renew: number
  churn_but_renew_percentage: number
  year_revenue: number
  month_revenue: number
  payment_users: number
  payment_users_percentage: number
  credit_cards: number
  credit_cards_percentage: number
  bank_transfers: number
  bank_transfers_percentage: number
}

export interface MonthlySummary {
  month: string
  small: number
  medium: number
  large: number
  enterprise: number
  revenue: number
}

export interface BusinessResponse {
  summary: SummaryReport
  monthly_revenue_summary: MonthlySummary[]
}

export const useBusinessReports = ({
  payment_type,
}: { payment_type?: string } = {}) => {
  return useQuery<BusinessResponse>({
    queryKey: [QUERY_KEYS.BUSINESS_REPORT, payment_type],
    enabled: true,
    queryFn: async () => {
      const { data } = await fetchAPI<BusinessResponse>({
        path: '/admins/reports/business_overview_report',
        params: payment_type ? { payment_type } : undefined,
      })
      return data
    },
  })
}

export interface RevenueDetailItem extends Record<string, unknown> {
  user_email: string
  company_name: string
  payment_type: string
  package: string
  period: string
  amount: number
}

export interface RevenueDetailsResponse {
  month: string
  year: number
  package: string
  total_amount: number
  count: number
  details: RevenueDetailItem[]
}

export const useRevenueDetails = ({
  month,
  package: packageName,
  year,
  package_recurring,
  payment_type,
  enabled = true,
}: {
  month?: number
  package?: string
  year?: number
  package_recurring?: string
  payment_type?: string
  enabled?: boolean
}) => {
  return useQuery<RevenueDetailsResponse>({
    queryKey: [
      QUERY_KEYS.REVENUE_DETAILS,
      month,
      packageName,
      year,
      package_recurring,
      payment_type,
    ],
    enabled: !!month && !!packageName && enabled,
    queryFn: async () => {
      const { data } = await fetchAPI<RevenueDetailsResponse>({
        path: '/admins/reports/revenue_details',
        params: {
          month,
          package: packageName,
          year,
          package_recurring,
          payment_type,
        },
      })
      return data
    },
  })
}
